import React, { useState, useEffect } from 'react';
import {
    Box,
    LinearProgress,
    Typography,
    Paper,
    Chip,
    Stack
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Real-time progress indicator for running copy operations
 * Polls the backend every 5 seconds to show live progress
 */
const RunProgressIndicator = ({ runId, srcProjectId, dstProjectId, onComplete, onProgressUpdate }) => {
    const [progress, setProgress] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        let interval;

        const fetchProgress = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(
                    `${API_BASE_URL}synctrack/autodesk/run-history/progress?runId=${runId}&srcProjectId=${srcProjectId}&dstProjectId=${dstProjectId}`,
                    {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Accept': 'application/json'
                        }
                    }
                );

                if (!response.ok) {
                    throw new Error('Failed to fetch progress');
                }

                const data = await response.json();
                setProgress(data);
                setLoading(false);

                // Notify parent of progress update
                if (onProgressUpdate) {
                    onProgressUpdate(data);
                }

                // Stop polling when complete
                if (!data.running || data.percentComplete >= 100) {
                    clearInterval(interval);
                    if (onComplete) {
                        onComplete(data);
                    }
                }
            } catch (err) {
                console.error('Error fetching progress:', err);
                setError(err.message);
                setLoading(false);
            }
        };

        // Initial fetch
        fetchProgress();

        // Poll every 5 seconds (reduced from 2 to reduce load)
        interval = setInterval(fetchProgress, 5000);

        return () => {
            if (interval) clearInterval(interval);
        };
    }, [runId, srcProjectId, dstProjectId, onComplete, onProgressUpdate]);

    if (loading) {
        return (
            <Box p={2}>
                <LinearProgress />
                <Typography variant="caption" align="center" display="block" mt={1}>
                    Loading progress...
                </Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Box p={2}>
                <Typography color="error" variant="body2">
                    Error: {error}
                </Typography>
            </Box>
        );
    }

    if (!progress) return null;

    return (
        <Paper elevation={2} sx={{ p: 2, mb: 2 }}>
            <Stack spacing={2}>
                {/* Progress Bar */}
                <Box>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography variant="body2" fontWeight="bold">
                            Copy Progress
                        </Typography>
                        <Typography variant="body2" color="primary" fontWeight="bold">
                            {progress.percentComplete?.toFixed(1)}%
                        </Typography>
                    </Box>
                    <LinearProgress
                        variant="determinate"
                        value={progress.percentComplete || 0}
                        sx={{ height: 8, borderRadius: 4 }}
                    />
                </Box>

                {/* File Counts */}
                <Box display="flex" gap={1} flexWrap="wrap">
                    <Chip
                        size="small"
                        label={`${progress.processedFiles || 0} / ${progress.totalFiles || 0} files`}
                        color="primary"
                        variant="outlined"
                    />
                    <Chip
                        size="small"
                        icon={<CheckCircleIcon />}
                        label={progress.successCount || 0}
                        color="success"
                        variant="outlined"
                    />
                    <Chip
                        size="small"
                        icon={<ErrorIcon />}
                        label={progress.failedCount || 0}
                        color="error"
                        variant="outlined"
                    />
                    <Chip
                        size="small"
                        icon={<SkipNextIcon />}
                        label={progress.skippedCount || 0}
                        color="warning"
                        variant="outlined"
                    />
                </Box>

                {/* Current File Being Copied */}
                {progress.currentFileName && (
                    <Box
                        sx={{
                            bgcolor: 'action.hover',
                            p: 1.5,
                            borderRadius: 1,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1
                        }}
                    >
                        <InsertDriveFileIcon color="action" fontSize="small" />
                        <Box flex={1}>
                            <Typography variant="caption" color="textSecondary" display="block">
                                Currently copying:
                            </Typography>
                            <Typography
                                variant="body2"
                                fontWeight="medium"
                                sx={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap'
                                }}
                                title={progress.currentFilePath}
                            >
                                {progress.currentFileName}
                            </Typography>
                            {progress.currentFileSize && (
                                <Typography variant="caption" color="textSecondary">
                                    {progress.currentFileSizeFormatted ||
                                        `${(progress.currentFileSize / (1024 * 1024)).toFixed(2)} MB`}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                )}

                {/* Status Message */}
                {progress.filesRemaining > 0 && (
                    <Typography variant="caption" color="textSecondary" align="center">
                        {progress.filesRemaining} files remaining
                    </Typography>
                )}
            </Stack>
        </Paper>
    );
};

export default RunProgressIndicator;
